import { THEME_TAGS } from '@/lib/themeTags';
import { findForeignThemeSyntax, findInvalidThemeTags } from '@/lib/themeTagValidation';

// ============================================================
// Conversion DÉTERMINISTE des balises « autre moteur » vers les
// balises Zifek — basée sur la documentation officielle des balises
// (ZIFEK_TEMPLATE_TAGS.md). Aucun appel IA : remplacement par table.
// Utilisé par le bouton « Corriger le fichier » du modal de référence,
// pour garantir que le contenu transmis à l'IA ne contient QUE des
// balises Zifek valides.
// ============================================================

const VALID_SET = new Set(THEME_TAGS.map((t) => t.name));

/** Table de correspondance des balises pointées (autre moteur → Zifek). */
const DOTTED_MAP: Record<string, string> = {
  'merchant.name': 'site_name',
  'shop.name': 'site_name',
  'store.name': 'site_name',
  'merchant.store_name': 'site_name',
  'merchant.description': 'site_description',
  'shop.description': 'site_description',
  'store.description': 'site_description',
  'merchant.logo': 'site_logo',
  'shop.logo': 'site_logo',
  'theme.logo': 'site_logo',
  'merchant.phone': 'site_phone',
  'merchant.telephone': 'site_phone',
  'shop.phone': 'site_phone',
  'merchant.email': 'site_email',
  'shop.email': 'site_email',
  'merchant.address': 'site_address',
  'merchant.city': 'site_address',
  'merchant.country': 'site_address',
  'shop.address': 'site_address',
  'merchant.about': 'site_about',
  'merchant.about_title': 'site_about',
  'merchant.about_description': 'site_about',
  'merchant.website': 'site_url',
  'site.copyright': 'year',
  'copyright': 'year',
  'footer.copyright': 'year',
  'featured_product.name': 'featured_products',
  'featured_product.price': 'featured_products',
  'featured_product.description': 'featured_products',
  'featured_product.image': 'featured_products',
  'products_page.title': 'products',
  'products_page.description': 'products',
  'homepage.hero.title': 'hero_banners',
  'homepage.hero.subtitle': 'hero_banners',
  'homepage.hero.description': 'hero_banners',
  'homepage.hero.button_text': 'hero_banners',
  'homepage.promotion.title': 'hero_banners',
  'homepage.promotion.description': 'hero_banners',
  'homepage.promotion.button_text': 'hero_banners',
  'product.name': 'products',
  'product.price': 'products',
  'product.image': 'products',
  'product.description': 'products',
  'product.url': 'products',
  'product.stock': 'products',
  'product.category': 'products',
  'blog.posts': 'blog_posts',
  'post.title': 'blog_posts',
  'post.content': 'blog_posts',
  'post.author': 'blog_posts',
  'post.date': 'blog_posts',
  'post.category': 'blog_posts',
  'post.image': 'blog_posts',
  'post.url': 'blog_posts',
  'team.members': 'team',
  'member.name': 'team',
  'member.photo': 'team',
  'member.role': 'team',
  'testimonial.name': 'testimonials',
  'testimonial.text': 'testimonials',
  'testimonial.rating': 'testimonials',
  'testimonial.avatar': 'testimonials',
  'testimonial.date': 'testimonials',
  'category.name': 'products',
  'category.image': 'products',
  'subcategory.name': 'products',
  'customer.name': 'site_name',
  'customer.email': 'site_email',
};

/** Alias de balises simples (mal orthographiées) → balise Zifek. */
const SIMPLE_ALIAS_MAP: Record<string, string> = {
  annee: 'year',
  year_only: 'year',
  copyright: 'year',
  nomdusite: 'site_name',
  nom: 'site_name',
  nom_boutique: 'site_name',
  boutique: 'site_name',
  telephone: 'site_phone',
  tel: 'site_phone',
  phone: 'site_phone',
  mail: 'site_email',
  adresse: 'site_address',
  adress: 'site_address',
  description: 'site_description',
  logo: 'site_logo',
};

function suggestDotted(name: string): string | null {
  const lower = name.toLowerCase();
  if (DOTTED_MAP[lower]) return DOTTED_MAP[lower];
  const head = lower.split('.')[0];
  switch (head) {
    case 'merchant':
    case 'shop':
    case 'store':
      return 'site_name';
    case 'product':
    case 'products':
    case 'cart':
    case 'category':
    case 'subcategory':
      return 'products';
    case 'post':
    case 'blog':
      return 'blog_posts';
    case 'team':
    case 'member':
      return 'team';
    case 'testimonial':
      return 'testimonials';
    case 'homepage':
      return 'hero_banners';
    case 'routes':
    case 'actions':
    case 'customer':
    case 'context':
    case 'cart_item':
    case 'variant':
    case 'order':
    case 'filters':
      return null;
    default:
      return null;
  }
}

export interface ConversionResult {
  content: string;
  /** Nombre de balises remplacées par une balise Zifek équivalente */
  replaced: number;
  /** Nombre de balises/marqueurs supprimés (aucun équivalent Zifek) */
  removed: number;
  /** Nombre de balises non-Zifek détectées au départ */
  detected: number;
}

/**
 * Convertit un contenu (instructions de référence ou HTML de page) vers les
 * balises Zifek officielles :
 *  1. Supprime les blocs de contrôle d'un autre moteur `{% … %}`.
 *  2. Supprime les marqueurs de section `{{#…}} {{/…}} {{^…}} {{else}}`.
 *  3. Remplace les balises pointées connues par leur équivalent Zifek.
 *  4. Remplace les balises simples mal orthographiées connues.
 *  5. Supprime toute balise restante non-Zifek.
 *  6. Conserve intégralement les balises Zifek valides (avec leurs attributs).
 */
export function convertToZifekTags(input: string): ConversionResult {
  if (!input) return { content: input || '', replaced: 0, removed: 0, detected: 0 };

  const detected =
    findForeignThemeSyntax(input).reduce((a, t) => a + t.count, 0) +
    findInvalidThemeTags(input).reduce((a, t) => a + t.count, 0);

  let replaced = 0;
  let removed = 0;
  let out = input;

  // 1. Contrôle Liquid {% ... %}
  out = out.replace(/\{%[\s\S]*?%\}/g, () => { removed += 1; return ''; });

  // 2. Marqueurs de section {{#...}} {{/...}} {{^...}} {{else}}
  out = out.replace(/\{\{\s*[#/^]\s*[\w.-]+[^}]*\}\}/g, () => { removed += 1; return ''; });
  out = out.replace(/\{\{\s*else\s*\}\}/gi, () => { removed += 1; return ''; });

  // 3+4+5. Balises {{ ... }} restantes
  out = out.replace(/\{\{\s*([\w.-]+)((?:\s+\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s}]+))*)\s*\}\}/g, (full, name: string, attrs: string) => {
    const lower = name.toLowerCase();
    if (!name.includes('.') && VALID_SET.has(name)) {
      // Balise Zifek valide → conservée à l'identique.
      return full;
    }
    if (name.includes('.')) {
      const mapped = suggestDotted(name);
      if (mapped) { replaced += 1; return `{{${mapped}}}`; }
      removed += 1;
      return '';
    }
    const alias = SIMPLE_ALIAS_MAP[lower];
    if (alias) { replaced += 1; return `{{${alias}}}`; }
    // Balise simple inconnue → tentative sur la suggestion la plus proche.
    removed += 1;
    return '';
  });

  // Nettoyage : lignes vides multiples créées par les suppressions.
  out = out.replace(/\n{3,}/g, '\n\n');

  return { content: out, replaced, removed, detected };
}

/** Vrai si un contenu mérite une correction (présence de balises non-Zifek). */
export function contentNeedsCorrection(content: string): boolean {
  return (
    findForeignThemeSyntax(content).length > 0 ||
    findInvalidThemeTags(content).length > 0
  );
}